import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import {
  loadTurnstileScript,
  TURNSTILE_ACTION,
  TURNSTILE_SITE_KEY,
} from "../../utils/turnstile";
import styles from "./index.module.scss";

export type TurnstileHandle = {
  getToken: () => string;
  reset: () => void;
};

type TurnstileProps = {
  action?: string;
};

const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(function Turnstile(
  { action = TURNSTILE_ACTION },
  ref
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const tokenRef = useRef("");
  const widgetIdRef = useRef<string | null>(null);

  useImperativeHandle(ref, () => ({
    getToken: () => tokenRef.current,
    reset: () => {
      tokenRef.current = "";
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !hostRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(hostRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          theme: "light",
          language: "es",
          callback: (value) => {
            tokenRef.current = value;
          },
          "expired-callback": () => {
            tokenRef.current = "";
          },
          "error-callback": () => {
            tokenRef.current = "";
          },
        });
      })
      .catch((err) => {
        console.error("[Turnstile] render:", err);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      tokenRef.current = "";
    };
  }, [action]);

  return <div ref={hostRef} className={styles.turnstileHost} />;
});

export default Turnstile;
